<template>
  <AdminCompanySectionComponent
    title="Corporate Billing"
    testId="company-billing"
    emptyText="Select a company to see its billing."
    errorText="Billing could not be loaded — saving is disabled so the existing setting stays unchanged."
    :company="selectedCompany"
    :form="billingForm"
  >
    <dl class="row small mb-3" data-testid="company-billing-summary">
      <dt class="col-sm-4">Onboarding prompt</dt>
      <dd class="col-sm-8">
        <span
          class="badge"
          :class="
            billing?.prompt_required
              ? 'badge-phoenix-warning'
              : 'badge-phoenix-success'
          "
        >
          {{
            billing?.prompt_required ? `Showing (${billing.step})` : "Not shown"
          }}
        </span>
      </dd>
      <dt class="col-sm-4">Annual consumption</dt>
      <dd class="col-sm-8">
        {{
          billing?.consumption_band
            ? CONSUMPTION_BAND_LABELS[billing.consumption_band]
            : "Not answered"
        }}
      </dd>
      <dt class="col-sm-4">Countries</dt>
      <dd class="col-sm-8">
        {{
          billing?.country_scope
            ? COUNTRY_SCOPE_LABELS[billing.country_scope]
            : "Not answered"
        }}
      </dd>
      <dt class="col-sm-4">Reporting standards</dt>
      <dd class="col-sm-8">
        {{ billing?.reporting_standards.join(", ") || "—" }}
        <span v-if="billing?.reporting_standard_other">
          ({{ billing.reporting_standard_other }})
        </span>
      </dd>
      <dt class="col-sm-4">Regions</dt>
      <dd class="col-sm-8">
        {{ billing?.consumption_regions.join(", ") || "—" }}
      </dd>
      <dt class="col-sm-4">Submitted</dt>
      <dd class="col-sm-8">
        {{
          billing?.qualified_at
            ? formatDateTimeIntoDate(billing.qualified_at)
            : "Not yet (answers save as they are given)"
        }}
      </dd>
      <dt class="col-sm-4">Plan</dt>
      <dd class="col-sm-8">{{ billing?.plan ?? "—" }}</dd>
      <dt class="col-sm-4">Subscription</dt>
      <dd class="col-sm-8">
        {{ billing?.subscription_status ?? "—" }}
        <span
          v-if="billing?.current_period_end && hasAccessUntilPeriodEnd"
          class="text-body-secondary"
        >
          (until {{ formatDateTimeIntoDate(billing.current_period_end) }})
        </span>
      </dd>
      <dt class="col-sm-4">Creem subscription</dt>
      <dd class="col-sm-8">{{ billing?.creem_subscription_id ?? "—" }}</dd>
      <dt class="col-sm-4">Demo requested</dt>
      <dd class="col-sm-8">
        {{
          billing?.demo_requested_at
            ? formatDateTimeIntoDate(billing.demo_requested_at)
            : "—"
        }}
      </dd>
      <dt class="col-sm-4">Onboarding call</dt>
      <dd class="col-sm-8" data-testid="onboarding-call-state">
        {{
          billing?.onboarding_call_booked_at
            ? `Booked ${formatDateTimeIntoDate(billing.onboarding_call_booked_at)}`
            : billing?.onboarding_call_requested_at
              ? `Booking page opened ${formatDateTimeIntoDate(billing.onboarding_call_requested_at)}, not confirmed`
              : billing?.onboarding_call_due
                ? "Not booked yet"
                : "—"
        }}
      </dd>
    </dl>

    <div class="mb-3 disabled-until-field">
      <label class="form-label" :for="disabledUntilId">
        Onboarded: unlock the dashboard until
      </label>
      <input
        :id="disabledUntilId"
        v-model="disabledUntil"
        type="date"
        class="form-control"
        data-testid="billing-prompt-disabled-until"
      />
      <div class="form-text">
        Set this when sales closes the deal with a corporate or trader client
        (or for an existing client): pick the date their agreement runs to. The
        onboarding prompt stays off through that day (Tallinn time). Clear it to
        lock the dashboard again.
      </div>
    </div>
  </AdminCompanySectionComponent>
</template>

<script setup lang="ts">
import type {
  AdminBillingStatus,
  ConsumptionBand,
  CountryScope,
} from "@/billing/billingTypes";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import AdminCompanySectionComponent from "@/dashboard/admin/AdminCompanySectionComponent.vue";
import { useCompanySectionForm } from "@/dashboard/admin/useCompanySectionForm";
import type { Company } from "@/types";
import { computed, ref, useId } from "vue";

const CONSUMPTION_BAND_LABELS: Record<ConsumptionBand, string> = {
  under_10_gwh: "Less than 10 GWh",
  over_10_gwh: "10 GWh or more",
};

const COUNTRY_SCOPE_LABELS: Record<CountryScope, string> = {
  one: "1 country",
  multiple: "More than 1 country",
};

const props = defineProps<{
  selectedCompany: Company | null;
}>();

const disabledUntilId = useId();

// A canceled or expired subscription has no access left, whatever period end Creem last reported.
const hasAccessUntilPeriodEnd = computed(
  () =>
    billing.value?.subscription_status === "active" ||
    billing.value?.subscription_status === "scheduled_cancel"
);
const billing = ref<AdminBillingStatus | null>(null);
// The date input's model: "" when cleared, which saves as null.
const disabledUntil = ref("");

const billingForm = useCompanySectionForm<AdminBillingStatus>({
  company: () => props.selectedCompany,
  resource: "billing",
  apply: (data) => {
    billing.value = data;
    disabledUntil.value = data.billing_prompt_disabled_until ?? "";
  },
  payload: () => ({
    billing_prompt_disabled_until: disabledUntil.value || null,
  }),
  savedMessage: ["Saved", "Onboarding setting updated."],
});
</script>

<style scoped lang="scss">
.disabled-until-field {
  max-width: 320px;
}
</style>
